import type { ConversationState } from './reducer.ts'

// Inverse edits contain changed properties and removed array spans only. They
// never contain a prior ConversationState or another undo journal.
export interface GuidanceUndoPatch {
  path: string[]
  existed?: boolean
  before?: unknown
  undefinedBefore?: boolean
  splice?: { index: number; deleteCount: number; afterLength: number; items: unknown[] }
}
export interface GuidanceUndoEntry { revision: number; action: string; patches: GuidanceUndoPatch[] }
export interface GuidanceUndoHistory { version: 1; revision: number; undoRevision?: number; entries: GuidanceUndoEntry[]; boundary: string | null; externalPending: boolean }

const mutable = new Set(['rep','researchObservation','knownSystemOrVendor','recommendedPlan','lowerPlan','price','appointmentSlots','confirmedNeed','confirmedDetectionGap','scopeSummary','currentMainNodeId','currentInterruptId','resumeNodeId','interruptMode','interruptStack','mainPathHistory','interruptHistory','coverageState','agencyState','protectionPriority','alertRoute','alertPreference','authorityState','schedulingRequest','callOutcome','resumeNotice','readinessConfirmed','providerDependencies','events','v9'])
const unsafe = new Set(['__proto__','prototype','constructor','guidanceUndo'])
const mutableV9 = new Set(['projectMode','projectQuoteRevision','projectAccepted','facts','frames','pendingConcerns','dependencies','main','step','serviceExplained','checkExplained','valuePresented','revision','superseded','quoteRevision','acceptedOffer','invitationOffer','factsLedger'])
const object = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value))
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
const copy = <T,>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value)) as T
export const guidanceRevision = (state: ConversationState): number => state.guidanceUndo?.revision ?? 0
const history = (state: ConversationState): GuidanceUndoHistory => state.guidanceUndo ?? { version: 1, revision: 0, entries: [], boundary: null, externalPending: false }

export function guidanceUndoBoundary(state: ConversationState): string | null {
  if (state.archived) return 'The call session has been archived.'
  if (state.completionIntent) return 'A canonical outcome or external action is pending completion.'
  if (state.scheduledContinuation) return 'An appointment has been confirmed.'
  if (state.v9?.payment === 'reported') return 'Payment has been reported; its external result must be preserved.'
  if (state.v9?.payment === 'verified') return 'The verified purchase must be preserved.'
  return null
}
export function canUndoGuidance(state: ConversationState): boolean {
  return state.guidanceVersion === 9 && !state.v9?.restoreIssue && !guidanceUndoBoundary(state) && !state.guidanceUndo?.externalPending && Boolean(state.guidanceUndo?.entries.length)
}
export function markGuidanceBoundary(state: ConversationState, reason: string, externalPending = false): ConversationState {
  const prior = history(state)
  return { ...state, guidanceUndo: { version: 1, revision: prior.revision + 1, undoRevision: prior.revision + 1, entries: [], boundary: reason, externalPending } }
}

function difference(before: unknown, after: unknown, path: string[], patches: GuidanceUndoPatch[], existed = true): void {
  if (equal(before, after)) return
  if (Array.isArray(before) && Array.isArray(after)) {
    let start = 0, end = 0
    while (start < before.length && start < after.length && equal(before[start], after[start])) start++
    while (end < before.length - start && end < after.length - start && equal(before[before.length - 1 - end], after[after.length - 1 - end])) end++
    patches.push({ path, splice: { index: start, deleteCount: after.length - start - end, afterLength: after.length, items: copy(before.slice(start, before.length - end)) } })
    return
  }
  if (object(before) && object(after)) {
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
      if (unsafe.has(key)) throw new Error('Guidance undo cannot record an unsafe property.')
      const had = Object.hasOwn(before, key), has = Object.hasOwn(after, key)
      if (had && has) difference(before[key], after[key], [...path, key], patches)
      else patches.push({ path: [...path, key], existed: had, ...(had ? before[key] === undefined ? { undefinedBefore: true } : { before: copy(before[key]) } : {}) })
    }
    return
  }
  patches.push({ path, existed, ...(before === undefined ? { undefinedBefore: true } : { before: copy(before) }) })
}
export function recordGuidanceUndo(before: ConversationState, after: ConversationState, action: string): ConversationState {
  if (before === after) return after
  const boundary = guidanceUndoBoundary(after)
  if (boundary) return markGuidanceBoundary(after, boundary)
  const prior = history(before), patches: GuidanceUndoPatch[] = []
  for (const key of mutable) {
    if (key === 'v9') {
      const a = before.v9, b = after.v9
      if (a && b) for (const field of new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof typeof a>) {
        if (!['payment','activation','restoreIssue'].includes(field)) difference(a[field], b[field], ['v9', field], patches)
      }
    } else difference(before[key as keyof ConversationState], after[key as keyof ConversationState], [key], patches, Object.hasOwn(before, key))
  }
  if (!patches.length) return after
  const revision = prior.revision + 1
  return { ...after, guidanceUndo: { version: 1, revision, undoRevision: prior.undoRevision ?? 0, boundary: prior.boundary, externalPending: prior.externalPending, entries: [...prior.entries, { revision, action, patches }] } }
}

export function undoGuidance(state: ConversationState): ConversationState {
  if (!canUndoGuidance(state)) return state
  return applyLastInverse(state)
}
function applyLastInverse(state: ConversationState): ConversationState {
  const prior = history(state), entry = prior.entries.at(-1)!
  const next = copy(state)
  for (const patch of [...entry.patches].reverse()) {
    let parent: Record<string, unknown> = next as unknown as Record<string, unknown>
    for (const key of patch.path.slice(0, -1)) {
      if (!Object.hasOwn(parent, key) || !object(parent[key])) throw new Error('Saved guidance undo no longer matches this session.')
      parent = parent[key]
    }
    const key = patch.path.at(-1)!
    if (patch.splice) {
      const array = parent[key], span = patch.splice
      if (!Array.isArray(array) || array.length !== span.afterLength || span.index + span.deleteCount > array.length) throw new Error('Saved guidance undo no longer matches this session.')
      array.splice(span.index, span.deleteCount, ...copy(span.items))
    } else if (!patch.existed) delete parent[key]
    else parent[key] = patch.undefinedBefore ? undefined : copy(patch.before)
  }
  next.guidanceUndo = { version: 1, revision: prior.revision + 1, undoRevision: prior.revision + 1, entries: prior.entries.slice(0, -1), boundary: prior.boundary, externalPending: prior.externalPending }
  return next
}

export function validGuidanceUndo(value: unknown): boolean {
  if (value === undefined) return true // Pre-upgrade snapshots have no reconstructable inverse history.
  if (!object(value) || value.version !== 1 || !Number.isSafeInteger(value.revision) || Number(value.revision) < 0 || !Array.isArray(value.entries) || typeof value.externalPending !== 'boolean' || value.boundary !== null && typeof value.boundary !== 'string') return false
  if (value.undoRevision !== undefined && (!Number.isSafeInteger(value.undoRevision) || Number(value.undoRevision) < 0 || Number(value.undoRevision) > Number(value.revision))) return false
  let revision = 0
  for (const entry of value.entries) {
    if (!object(entry) || !Number.isSafeInteger(entry.revision) || Number(entry.revision) <= revision || Number(entry.revision) > Number(value.revision) || typeof entry.action !== 'string' || !Array.isArray(entry.patches) || !entry.patches.length) return false
    revision = Number(entry.revision)
    for (const patch of entry.patches) {
      if (!object(patch) || !Array.isArray(patch.path) || !patch.path.length || patch.path.some(key => typeof key !== 'string' || unsafe.has(key)) || !mutable.has(patch.path[0])) return false
      if (patch.path[0] === 'v9' && (patch.path.length < 2 || !mutableV9.has(patch.path[1]))) return false
      if (patch.splice) {
        const span = patch.splice
        if (!object(span) || !['index','deleteCount','afterLength'].every(key => Number.isSafeInteger(span[key]) && Number(span[key]) >= 0) || !Array.isArray(span.items) || !safeValue(span.items) || Number(span.index) + Number(span.deleteCount) > Number(span.afterLength)) return false
      } else if (typeof patch.existed !== 'boolean' || patch.undefinedBefore !== undefined && typeof patch.undefinedBefore !== 'boolean' || patch.existed && !patch.undefinedBefore && !Object.hasOwn(patch, 'before') || Object.hasOwn(patch, 'before') && !safeValue(patch.before)) return false
    }
  }
  return true
}
function safeValue(value: unknown, depth = 0): boolean {
  // This is a data-shape bound, not a limit on concerns or undo entries. The
  // interpreter's stored records are shallow JSON; recursive snapshots fail.
  if (depth > 16) return false
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true
  if (typeof value === 'number') return Number.isFinite(value)
  if (Array.isArray(value)) return value.every(item => safeValue(item, depth + 1))
  return object(value) && Object.entries(value).every(([key, item]) => !unsafe.has(key) && safeValue(item, depth + 1))
}
export function validGuidanceUndoChain(state: ConversationState, validate: (state: ConversationState) => boolean): boolean {
  if (!validGuidanceUndo(state.guidanceUndo)) return false
  try {
    let previous = state
    while (previous.guidanceUndo?.entries.length) {
      previous = applyLastInverse(previous)
      if (!validate(previous)) return false
    }
    return true
  } catch { return false }
}
